// frontend/src/utils/practitionerFormUtils.js

import { safeText, validateNumberField } from "@utils/utils";

// Dropdown options for all 16 practitioner types.
// Previously had only dds and hyg; 14 new entries added to match TYPE_CHOICES in models.py.
// The blank entry is kept for forms that show a "Select type..." placeholder row.
export const TYPE_OPTIONS = [
  { value: "", label: "Select type..." },
  { value: "dds", label: "DDS — Dentist" },
  { value: "hyg", label: "HYG — Hygienist" },
  { value: "pl", label: "PL — Practice Lead" },
  { value: "am", label: "AM — Area Manager" },
  { value: "rcp", label: "RCP — Receptionist" },
  { value: "rc", label: "RC — Recall Coordinator" },
  { value: "tc", label: "TC — Treatment Coordinator" },
  { value: "dal1", label: "DAL1 — Dental Assistant L1" },
  { value: "dal2", label: "DAL2 — Dental Assistant L2" },
  { value: "hygr", label: "HYGR — Hygienist Restorative" },
  { value: "nur", label: "NUR — Nurse" },
  { value: "rn", label: "RN" },
  { value: "ft", label: "FT — Floater" },
  { value: "str", label: "STR — Sterilization" },
  { value: "srg", label: "SRG — Surgeon" },
  { value: "spc", label: "SPC — Specialist" },
];

export const RATE_TYPE_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "hourly", label: "Hourly" },
  { value: "salary", label: "Salary" },
  { value: "daily", label: "Daily" },
];

export const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const PAID_BASED_ON_OPTIONS = [
  { value: "", label: "Select..." },
  { value: "collection", label: "Collection" },
  { value: "production", label: "Production" },
];

export const PREFIX_OPTIONS = [
  { value: "", label: "None" },
  { value: "Dr.", label: "Dr." },
];

export const GRID_MAX_ROWS = 4;
export const OTHER_PROFIT_MAX_ROWS = 4;
export const OTHER_DEDUCTION_MAX_ROWS = 4;

export const INITIAL_PRACTITIONER_FORM = {
  prefix: "",
  first_name: "",
  last_name: "",
  practitioner_type: "",
  status: "active",
  actual_name: "",
  email: "",
  provider_code: "",
  // Identity/employment fields (not financial — visible to all roles)
  position_id: "",
  associate_id: "",
  rate_type: "",
  hire_date: "",
  supervisor: "",
  paid_based_on: "",

  associate_fee_percentage: "",

  per_day: false,
  per_day_value: "",
  insurance: "",
  grid: false,
  include_hyg: false,

  // Other profit (checkbox + tiered rows of { label, percent }, stored as JSON string array)
  other_profit_enabled: false,
  other_profit_rows: Array.from({ length: OTHER_PROFIT_MAX_ROWS }, () => ({
    label: "",
    percent: "",
  })),
  __other_profit_rows_visible: 1,

  // Other deduction (checkbox + tiered rows of { label, mode, hour_rate },
  // stored as JSON string array). `mode` picks how the month's figure is
  // entered: an hourly rate here × hours entered per month, or the exact
  // dollar amount entered per month.
  other_deduction_enabled: false,
  other_deduction_rows: Array.from({ length: OTHER_DEDUCTION_MAX_ROWS }, () => ({
    label: "",
    mode: "hourly",
    hour_rate: "",
  })),
  __other_deduction_rows_visible: 1,

  base_pay_1: "",
  base_pay_1_percentage: "",
  base_pay_2: "",
  base_pay_2_percentage: "",
  base_pay_3: "",
  base_pay_3_percentage: "",
  base_pay_4: "",
  base_pay_4_percentage: "",

  // NetSuite IDs (admin/organizer only)
  netsuite_vendor_id: "",
  netsuite_revenue_id: "",
  netsuite_af_expense_id: "",
  netsuite_lab_expense_id: "",
  netsuite_invisalign_id: "",

  __grid_rows_forced: "1",
};

/**
 * Compensation / insurance keys on the create form. Hidden from Practitioner
 * Lead (role 4) the same way ProfilePractitionerTab and the edit modal hide
 * them; CreatePractitionerSection resets these to INITIAL_PRACTITIONER_FORM
 * values before building the payload so a hidden field never sets data.
 */
export const PRACTITIONER_FINANCIAL_FORM_KEYS = [
  "paid_based_on",
  "associate_fee_percentage",
  "per_day",
  "per_day_value",
  "insurance",
  "grid",
  "include_hyg",
  "other_profit_enabled",
  "other_profit_rows",
  "__other_profit_rows_visible",
  "other_deduction_enabled",
  "other_deduction_rows",
  "__other_deduction_rows_visible",
  "base_pay_1",
  "base_pay_1_percentage",
  "base_pay_2",
  "base_pay_2_percentage",
  "base_pay_3",
  "base_pay_3_percentage",
  "base_pay_4",
  "base_pay_4_percentage",
  "__grid_rows_forced",
];

export const PRACTITIONER_TEXT_FIELDS = [
  {
    key: "first_name",
    label: "First Name",
    placeholder: "e.g. John",
    required: true,
    colSpan: 1,
  },
  {
    key: "last_name",
    label: "Last Name",
    placeholder: "e.g. Smith",
    required: true,
    colSpan: 1,
  },
  {
    key: "actual_name",
    label: "Corporation Name",
    placeholder: "Optional",
    colSpan: 1,
  },
  {
    key: "email",
    label: "Email",
    placeholder: "Optional",
    colSpan: 1,
    type: "email",
  },
  {
    key: "provider_code",
    label: "Provider Code",
    placeholder: "Optional",
    colSpan: 1,
  },
  // Identity/employment fields rendered after Provider Code
  {
    key: "position_id",
    label: "Position ID",
    placeholder: "Optional",
    colSpan: 1,
  },
  {
    key: "associate_id",
    label: "Associate ID",
    placeholder: "Optional",
    colSpan: 1,
  },
  // rate_type and hire_date are intentionally excluded here — they require a
  // Dropdown and a date input respectively, so they're rendered explicitly in
  // CreatePractitionerSection after this list.
];

export const PRACTITIONER_CHECKBOX_FIELDS = [
  { key: "per_day", label: "Paid By Day" },
  { key: "grid", label: "Grid" },
];

/**
 * NetSuite ID fields — admin/organizer only. Each must be a positive integer (> 0).
 */
export const PRACTITIONER_NETSUITE_FIELDS = [
  { key: "netsuite_vendor_id", label: "NetSuite Vendor ID" },
  { key: "netsuite_revenue_id", label: "NetSuite Revenue ID" },
  { key: "netsuite_af_expense_id", label: "NetSuite AF Expense ID" },
  { key: "netsuite_lab_expense_id", label: "NetSuite Lab Expense ID" },
  { key: "netsuite_invisalign_id", label: "NetSuite Invisalign ID" },
];

export const PRACTITIONER_NETSUITE_FIELD_KEYS = PRACTITIONER_NETSUITE_FIELDS.map((f) => f.key);

/**
 * Validate a NetSuite ID input string.
 * Empty -> { ok: true, hasAny: false, n: null } (allowed; clears).
 * Non-empty must be a positive integer (> 0).
 */
export function validateNetsuiteId(raw) {
  const s = safeText(raw).trim();
  if (s === "") return { ok: true, hasAny: false, n: null };
  if (!/^\d+$/.test(s)) return { ok: false, hasAny: true, n: null };
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return { ok: false, hasAny: true, n: null };
  return { ok: true, hasAny: true, n };
}

export function validateNetsuiteIds(form) {
  const out = {};
  let allOk = true;
  for (const { key } of PRACTITIONER_NETSUITE_FIELDS) {
    const meta = validateNetsuiteId(form?.[key]);
    out[key] = meta;
    if (meta.hasAny && !meta.ok) allOk = false;
  }
  return { ok: allOk, fields: out };
}

export function buildDisplayName(form) {
  const prefix = safeText(form.prefix).trim();
  const first = safeText(form.first_name).trim();
  const last = safeText(form.last_name).trim();
  return [prefix, first, last].filter(Boolean).join(" ").trim();
}

/**✅ These are the 3 helpers (use for 3 of them)-base pay, base pay %, associate fee %*/
export function validateBasePay(raw, hasAnyRowInput) {
  if (!hasAnyRowInput) return { ok: true, num: null };
  const res = validateNumberField(raw, { min: 0 });
  // if row has any input, base pay MUST be present + valid
  return { ok: res.ok && res.num !== null, num: res.num };
}

export function validateBasePayPct(raw, hasAnyRowInput) {
  if (!hasAnyRowInput) return { ok: true, num: null };
  const res = validateNumberField(raw, { min: 0, max: 100 });
  // if row has any input, pct MUST be present + valid
  return { ok: res.ok && res.num !== null, num: res.num };
}

export function validateAssociateFeePct(raw) {
  // associate fee: allow empty, otherwise must be 0-100
  return validateNumberField(raw, { min: 0, max: 100 });
}

/**
 * Validate a single other-profit tier row.
 * Non-empty row: label required, percent strictly 0 < n <= 100.
 * Empty row: valid (ignored on serialize).
 */
export function validateOtherProfitRow(row) {
  const label = safeText(row?.label).trim();
  const pStr = safeText(row?.percent).trim();
  const hasAny = label !== "" || pStr !== "";
  if (!hasAny) return { ok: true, hasAny: false, label: "", percent: null };
  const n = Number(pStr);
  const ok = label !== "" && pStr !== "" && Number.isFinite(n) && n > 0 && n <= 100;
  return { ok, hasAny: true, label, percent: ok ? n : null };
}

/**
 * Aggregate validator across enabled tier rows.
 * When disabled: always valid.
 * Otherwise: at least one row must be filled AND every filled row must be valid.
 */
export function validateOtherProfit({ enabled, rows }) {
  if (!enabled) return { ok: true, hasAny: false, rows: [] };
  const list = Array.isArray(rows) ? rows : [];
  const metas = list.map(validateOtherProfitRow);
  const filled = metas.filter((m) => m.hasAny);
  if (filled.length === 0) return { ok: false, hasAny: false, rows: [] };
  const allOk = filled.every((m) => m.ok);
  return {
    ok: allOk,
    hasAny: true,
    rows: allOk ? filled.map((m) => ({ label: m.label, percent: m.percent })) : [],
  };
}

/**
 * Parse backend `other_profit` (JSON string) into tier rows.
 * Supports:
 *   - Array of { label, percent }  (preferred new shape)
 *   - Single object { label, percent } (legacy)
 * Returns { enabled, rows: [{label, percent: string}] }.
 */
export function parseOtherProfit(raw) {
  const empty = { enabled: false, rows: [] };
  const s = safeText(raw).trim();
  if (!s) return empty;

  let parsed;
  try {
    parsed = JSON.parse(s);
  } catch {
    // Raw non-JSON text — surface as single label row so user can fix.
    return { enabled: true, rows: [{ label: s, percent: "" }] };
  }

  const toRow = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    const label = safeText(obj.label);
    const pct = obj.percent === undefined || obj.percent === null ? "" : String(obj.percent);
    if (label === "" && pct === "") return null;
    return { label, percent: pct };
  };

  if (Array.isArray(parsed)) {
    const rows = parsed.map(toRow).filter(Boolean);
    return rows.length > 0 ? { enabled: true, rows } : empty;
  }
  if (parsed && typeof parsed === "object") {
    const row = toRow(parsed);
    return row ? { enabled: true, rows: [row] } : empty;
  }
  return empty;
}

/**
 * Serialize other_profit tier rows for the backend TextField.
 * Returns a JSON array string when enabled+valid, or null to clear.
 */
export function serializeOtherProfit({ enabled, rows }) {
  const meta = validateOtherProfit({ enabled, rows });
  if (!meta.hasAny || !meta.ok) return null;
  return JSON.stringify(meta.rows.map((r) => ({ label: r.label, percent: r.percent })));
}

/**
 * How one other-deduction row reaches its dollar amount.
 *  - "hourly": the profile holds an $/hr rate; the monthly form asks for hours.
 *  - "amount": nothing is held on the profile; the monthly form asks for the
 *    exact dollar amount to deduct.
 * Rows saved before the fixed-amount option carry no mode and are hourly.
 */
export const OTHER_DEDUCTION_MODE_HOURLY = "hourly";
export const OTHER_DEDUCTION_MODE_AMOUNT = "amount";

export function normalizeOtherDeductionMode(mode) {
  return safeText(mode).trim().toLowerCase() === OTHER_DEDUCTION_MODE_AMOUNT
    ? OTHER_DEDUCTION_MODE_AMOUNT
    : OTHER_DEDUCTION_MODE_HOURLY;
}

/**
 * Validate a single other-deduction tier row.
 * Non-empty row: label required; an hourly row also needs a non-negative
 * hour_rate. A fixed-amount row carries no rate at all — the amount is entered
 * per month, so there is nothing else to check here.
 * Empty row: valid (ignored on serialize).
 */
export function validateOtherDeductionRow(row) {
  const label = safeText(row?.label).trim();
  const mode = normalizeOtherDeductionMode(row?.mode);
  const rStr = safeText(row?.hour_rate).trim();

  if (mode === OTHER_DEDUCTION_MODE_AMOUNT) {
    const hasAny = label !== "";
    if (!hasAny) return { ok: true, hasAny: false, label: "", mode, hour_rate: null };
    return { ok: true, hasAny: true, label, mode, hour_rate: null };
  }

  const hasAny = label !== "" || rStr !== "";
  if (!hasAny) return { ok: true, hasAny: false, label: "", mode, hour_rate: null };
  const n = Number(rStr);
  const ok = label !== "" && rStr !== "" && Number.isFinite(n) && n >= 0;
  return { ok, hasAny: true, label, mode, hour_rate: ok ? n : null };
}

/**
 * Aggregate validator across enabled deduction tier rows.
 */
export function validateOtherDeduction({ enabled, rows }) {
  if (!enabled) return { ok: true, hasAny: false, rows: [] };
  const list = Array.isArray(rows) ? rows : [];
  const metas = list.map(validateOtherDeductionRow);
  const filled = metas.filter((m) => m.hasAny);
  if (filled.length === 0) return { ok: false, hasAny: false, rows: [] };
  const allOk = filled.every((m) => m.ok);
  return {
    ok: allOk,
    hasAny: true,
    rows: allOk
      ? filled.map((m) => ({ label: m.label, mode: m.mode, hour_rate: m.hour_rate }))
      : [],
  };
}

/**
 * Parse backend `other_deduction` (JSON string) into tier rows shaped
 * `{ label, mode, hour_rate: string }`. Legacy entries shaped `{label, percent}`
 * are converted: percent is dropped and hour_rate seeds blank so the user
 * is prompted to re-enter the new value.
 */
export function parseOtherDeduction(raw) {
  const empty = { enabled: false, rows: [] };
  const s = safeText(raw).trim();
  if (!s) return empty;

  let parsed;
  try {
    parsed = JSON.parse(s);
  } catch {
    return {
      enabled: true,
      rows: [{ label: s, mode: OTHER_DEDUCTION_MODE_HOURLY, hour_rate: "" }],
    };
  }

  const toRow = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    const label = safeText(obj.label);
    const mode = normalizeOtherDeductionMode(obj.mode);
    // A fixed-amount row has no rate to carry, whatever an older save left behind.
    const rate =
      mode === OTHER_DEDUCTION_MODE_AMOUNT || obj.hour_rate === undefined || obj.hour_rate === null
        ? ""
        : String(obj.hour_rate);
    if (label === "" && rate === "") return null;
    return { label, mode, hour_rate: rate };
  };

  if (Array.isArray(parsed)) {
    const rows = parsed.map(toRow).filter(Boolean);
    return rows.length > 0 ? { enabled: true, rows } : empty;
  }
  if (parsed && typeof parsed === "object") {
    const row = toRow(parsed);
    return row ? { enabled: true, rows: [row] } : empty;
  }
  return empty;
}

/**
 * Serialize other_deduction tier rows for the backend TextField.
 * Returns a JSON array string when enabled+valid, or null to clear.
 */
export function serializeOtherDeduction({ enabled, rows }) {
  const meta = validateOtherDeduction({ enabled, rows });
  if (!meta.hasAny || !meta.ok) return null;
  return JSON.stringify(
    meta.rows.map((r) =>
      r.mode === OTHER_DEDUCTION_MODE_AMOUNT
        ? { label: r.label, mode: OTHER_DEDUCTION_MODE_AMOUNT }
        : { label: r.label, mode: OTHER_DEDUCTION_MODE_HOURLY, hour_rate: r.hour_rate },
    ),
  );
}

/**
 * The mode a SAVED monthly entry was recorded under.
 * Entries written before `mode` existed are read from their shape.
 */
export function savedOtherDeductionMode(saved) {
  if (!saved || typeof saved !== "object") return null;
  const explicit = safeText(saved.mode).trim().toLowerCase();
  if (explicit === OTHER_DEDUCTION_MODE_AMOUNT || explicit === OTHER_DEDUCTION_MODE_HOURLY) {
    return explicit;
  }
  if (saved.hour_rate !== undefined || saved.hours !== undefined) {
    return OTHER_DEDUCTION_MODE_HOURLY;
  }
  if (saved.amount !== undefined) return OTHER_DEDUCTION_MODE_AMOUNT;
  return null;
}

/**
 * Overlay a month's SAVED deduction entries onto the practitioner's current
 * tier config, so a month already on the books keeps reading and re-saving the
 * way it was recorded.
 *
 * The tier list itself belongs to the profile — that is what decides which
 * rows a month has. But the mode and the hourly rate are properties of the
 * figure that was entered: if the profile later switches a tier to a fixed
 * amount, or changes its $/hr, an already-saved month must not silently
 * reinterpret or re-price its own number. Only months with nothing saved for a
 * tier follow the profile's current setting.
 */
export function resolveOtherDeductionTiers(tiers, savedEntries) {
  const list = Array.isArray(tiers) ? tiers : [];
  const saved = Array.isArray(savedEntries) ? savedEntries : [];
  if (list.length === 0 || saved.length === 0) return list;

  const byLabel = new Map();
  saved.forEach((entry) => {
    if (entry && entry.label) byLabel.set(String(entry.label), entry);
  });

  return list.map((tier) => {
    const match = byLabel.get(String(tier?.label));
    const mode = savedOtherDeductionMode(match);
    if (!mode) return tier;
    if (mode === OTHER_DEDUCTION_MODE_AMOUNT) {
      return { ...tier, mode: OTHER_DEDUCTION_MODE_AMOUNT };
    }
    return {
      ...tier,
      mode: OTHER_DEDUCTION_MODE_HOURLY,
      hour_rate:
        match.hour_rate === undefined || match.hour_rate === null
          ? tier?.hour_rate
          : match.hour_rate,
    };
  });
}

/**
 * The value a monthly form should show for one deduction tier, given whatever
 * was saved on that month's record.
 *
 * The input means different things per mode — hours for an hourly tier, dollars
 * for a fixed one — so the field it reads back differs too. `input_value` is
 * the oldest saved key and only ever held an hourly-style quantity.
 */
export function otherDeductionInputFromSaved(tier, saved) {
  if (!saved || typeof saved !== "object") return "";
  const isAmount = normalizeOtherDeductionMode(tier?.mode) === OTHER_DEDUCTION_MODE_AMOUNT;
  const candidates = isAmount ? [saved.amount] : [saved.hours, saved.input_value];
  const v = candidates.find((c) => c !== undefined && c !== null && c !== "");
  return v === undefined ? "" : v;
}

/**
 * Build the `other_deduction` entry a monthly form submits for one tier.
 * An hourly tier snapshots the profile's rate alongside the hours entered, so
 * the record still explains itself after the profile changes; a fixed tier
 * carries only the amount.
 */
export function buildOtherDeductionEntry(tier, rawInput) {
  const label = safeText(tier?.label);
  const n = rawInput === "" || rawInput === undefined || rawInput === null ? 0 : Number(rawInput);
  const entered = Number.isFinite(n) ? n : 0;
  if (normalizeOtherDeductionMode(tier?.mode) === OTHER_DEDUCTION_MODE_AMOUNT) {
    return { label, mode: OTHER_DEDUCTION_MODE_AMOUNT, amount: entered };
  }
  return {
    label,
    mode: OTHER_DEDUCTION_MODE_HOURLY,
    hour_rate: Number(tier?.hour_rate) || 0,
    hours: entered,
  };
}

export function validateEmail(raw) {
  const s = safeText(raw).trim();
  if (s === "") return { ok: true, hasAny: false };
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  return { ok: valid, hasAny: true };
}

/**
 * ✅ Keeps older callers working.
 * If other files import toNumberOrNull, keep it.
 * It now relies on the ONE validation function above.
 */
export function toNumberOrNull(v) {
  const res = validateNumberField(v);
  return res.ok ? res.num : null;
}

/* ✅ BACKWARDS-COMPAT EXPORTS (prevents compile errors)
   These DO NOT duplicate logic: they wrap validateNumberField (single source)
/** older imports may still expect these */
export function isValidPay(n) {
  if (n === null) return true;
  return Number.isFinite(n) && n >= 0;
}

export function isValidPct(n) {
  if (n === null) return true;
  return Number.isFinite(n) && n >= 0 && n <= 100;
}

/** older callers expected shape: { hasAny, n, ok } */
export function validatePayField(raw) {
  const s = safeText(raw).trim();
  const hasAny = s !== "";
  const res = validateNumberField(raw, { min: 0 });
  const ok = !hasAny ? true : res.ok && res.num !== null;
  return { hasAny, n: res.num, ok };
}

/** older callers expected shape: { hasAny, n, ok } */
export function validatePctField(raw) {
  const s = safeText(raw).trim();
  const hasAny = s !== "";
  const res = validateNumberField(raw, { min: 0, max: 100 });
  const ok = !hasAny ? true : res.ok && res.num !== null;
  return { hasAny, n: res.num, ok };
}

export function extractFirstValidGridRow(form) {
  for (let i = 1; i <= GRID_MAX_ROWS; i++) {
    const payKey = `base_pay_${i}`;
    const pctKey = `base_pay_${i}_percentage`;

    const payRaw = safeText(form[payKey]).trim();
    const pctRaw = safeText(form[pctKey]).trim();
    const hasAny = payRaw !== "" || pctRaw !== "";
    if (!hasAny) continue;

    const payMeta = validateBasePay(form[payKey], hasAny);
    const pctMeta = validateBasePayPct(form[pctKey], hasAny);

    if (!payMeta.ok || !pctMeta.ok) continue;

    return { base_pay: payMeta.num, pay_percent: pctMeta.num };
  }
  return null;
}

export function computeGridIsValid(form) {
  if (!form.grid) return true;

  for (let i = 1; i <= GRID_MAX_ROWS; i++) {
    const payKey = `base_pay_${i}`;
    const pctKey = `base_pay_${i}_percentage`;

    const payRaw = safeText(form[payKey]).trim();
    const pctRaw = safeText(form[pctKey]).trim();
    const hasAny = payRaw !== "" || pctRaw !== "";
    if (!hasAny) continue;

    const payMeta = validateBasePay(form[payKey], hasAny);
    const pctMeta = validateBasePayPct(form[pctKey], hasAny);

    if (!payMeta.ok || !pctMeta.ok) return false;
  }

  return true;
}

export function computeEffectiveGridRows(form) {
  if (!form.grid) return 0;

  const forced = Number(safeText(form.__grid_rows_forced).trim());
  if (Number.isFinite(forced) && forced >= 1) {
    return Math.min(GRID_MAX_ROWS, forced);
  }

  let maxFilled = 1;
  for (let i = 1; i <= GRID_MAX_ROWS; i++) {
    const pay = safeText(form[`base_pay_${i}`]).trim();
    const pct = safeText(form[`base_pay_${i}_percentage`]).trim();
    if (pay !== "" || pct !== "") maxFilled = i;
  }

  return Math.max(1, maxFilled);
}

/**
 * ✅ MATCH BACKEND:
 * - base_pay only when grid=true and rows valid
 * - associate_fee_percentage must be number 0..100 or omitted if empty
 */
export function buildCreatePractitionerPayload({ clinicId, form }) {
  const payload = {
    clinic: clinicId,
    prefix: safeText(form.prefix).trim() || "",
    first_name: safeText(form.first_name).trim(),
    last_name: safeText(form.last_name).trim(),
    practitioner_type: safeText(form.practitioner_type).trim().toLowerCase(),
    status: (safeText(form.status).trim() || "active").toLowerCase(),

    per_day: !!form.per_day,
    grid: !!form.grid,
    include_hyg: !!form.include_hyg,

    per_day_value: form.per_day ? toNumberOrNull(form.per_day_value) : null,
    insurance: toNumberOrNull(form.insurance),
  };

  // Optional text fields
  if (safeText(form.actual_name).trim()) payload.actual_name = safeText(form.actual_name).trim();
  if (safeText(form.email).trim()) payload.email = safeText(form.email).trim();
  if (safeText(form.provider_code).trim())
    payload.provider_code = safeText(form.provider_code).trim();
  // Identity/employment fields
  if (safeText(form.position_id).trim()) payload.position_id = safeText(form.position_id).trim();
  if (safeText(form.associate_id).trim()) payload.associate_id = safeText(form.associate_id).trim();
  if (safeText(form.rate_type).trim())
    payload.rate_type = safeText(form.rate_type).trim().toLowerCase();
  if (safeText(form.hire_date).trim()) payload.hire_date = safeText(form.hire_date).trim();
  if (safeText(form.supervisor).trim()) payload.supervisor = safeText(form.supervisor).trim();
  if (safeText(form.paid_based_on).trim())
    payload.paid_based_on = safeText(form.paid_based_on).trim();

  // base_pay rows only when grid=true AND valid
  if (payload.grid) {
    const rows = [];
    for (let i = 1; i <= GRID_MAX_ROWS; i++) {
      const payKey = `base_pay_${i}`;
      const pctKey = `base_pay_${i}_percentage`;

      const payRaw = safeText(form[payKey]).trim();
      const pctRaw = safeText(form[pctKey]).trim();
      const hasAny = payRaw !== "" || pctRaw !== "";
      if (!hasAny) continue;

      const payMeta = validateBasePay(form[payKey], hasAny);
      const pctMeta = validateBasePayPct(form[pctKey], hasAny);

      if (payMeta.ok && pctMeta.ok) {
        rows.push({ base_pay: payMeta.num, pay_percent: pctMeta.num });
      }
    }
    if (rows.length > 0) payload.base_pay = rows;
  }

  // ✅ Associate fee %
  const feeMeta = validateAssociateFeePct(form.associate_fee_percentage);
  if (feeMeta.ok && feeMeta.num !== null) {
    payload.associate_fee_percentage = feeMeta.num;
  }

  // ✅ Other profit — JSON string stored server-side (TextField)
  const otherProfitSerialized = serializeOtherProfit({
    enabled: !!form.other_profit_enabled,
    rows: form.other_profit_rows,
  });
  if (otherProfitSerialized !== null) {
    payload.other_profit = otherProfitSerialized;
  }

  // ✅ Other deduction — JSON string stored server-side (TextField)
  const otherDeductionSerialized = serializeOtherDeduction({
    enabled: !!form.other_deduction_enabled,
    rows: form.other_deduction_rows,
  });
  if (otherDeductionSerialized !== null) {
    payload.other_deduction = otherDeductionSerialized;
  }

  // ✅ NetSuite IDs (admin only). Caller is responsible for only sending the form
  // with these populated for admin/organizer; we still defensively validate here
  // and only include positive-integer values.
  for (const { key } of PRACTITIONER_NETSUITE_FIELDS) {
    const meta = validateNetsuiteId(form?.[key]);
    if (meta.hasAny && meta.ok) {
      payload[key] = String(meta.n);
    }
  }

  return payload;
}
